import { FetcherConfig, PaginatedData } from '@seller-hermes/app/types';
import { Seller, SellerCarrier } from '@seller-hermes/features/seller';
import {
  SellerCountries,
  SellerDocumentTypes,
} from '@seller-hermes/features/sellers/new-seller-dialog/new-seller-dialog.schema';
import api from '@seller-hermes/utils/api';

export interface CreateSellerParams {
  email: string;
  phone: string;
  document_type: SellerDocumentTypes;
  document_number: string;
  trading_name: string;
  identifier?: string;
  state_registration?: string | null;
  tester?: boolean;
  country: SellerCountries;
  timezone: string;
  theme_id?: string;
  active?: boolean;
  address: {
    street: string;
    street_number: string;
    complement?: string;
    reference?: string;
    neighborhood: string;
    zip_code?: string;
    city: string;
    state: string;
    latitude?: number;
    longitude?: number;
  };
  operation_hours?: {
    [key: string]: {
      open: string;
      close: string;
    };
  } | null;
}

interface FeatureConfig {
  use_new_route?: boolean;
  private_fleet?: {
    delivery_price?: number;
    expected_delivery_minutes?: number;
    operation_start_time?: string;
    operation_finish_time?: string;
  };
}

export interface UpdateSellerParams
  extends Omit<Partial<CreateSellerParams>, 'address'> {
  id: string;
  feature_config?: null | FeatureConfig;
  notification_preferences_id?: string;
}

interface SellerFilter {
  seller_ids: string[];
  filters: {
    'carrier.seller_registration_required': boolean;
  };
}

interface AddressElements {
  street: string;
  street_number: string;
  complement?: string;
  neighborhood: string;
  zip_code: string;
  city: string;
  state: string;
  country?: string;
  provider?: string | null;
}

interface UpdateAddressParams extends AddressElements {
  id: string;
  reference?: string;
  latitude?: number;
  longitude?: number;
}

export class SellersQuery {
  static create = async (body: CreateSellerParams) => {
    return api.post('/seller', body).then((res) => res.data);
  };

  static search = async (
    body: FetcherConfig,
  ): Promise<PaginatedData<Seller[]>> => {
    return api.post('/v2/search/sellers', body).then((res) => res.data);
  };

  // ATENÇÃO: pagina em `size: 1000`. NÃO use como "todas as filiais do usuário"
  // — grupos maiores que isso vêm truncados silenciosamente (foi o SUP-425).
  // Pra lista completa do escopo do usuário, use `lookup()`.
  static getAll = async (
    seller_ids?: string[],
  ): Promise<PaginatedData<Seller[]>> => {
    return api
      .post('/v2/search/sellers', {
        seller_ids: seller_ids || [],
        filters: {},
        from: 0,
        size: 1000,
        sort: {},
      })
      .then((res) => res.data);
  };

  // Projeção mínima pro seletor de filiais (GET /v2/sellers/lookup).
  // Devolve só id/identifier/trading_name/document_number/active +
  // warehouses[].address_object (city/state/etc) + seller_seller_tags[].
  // seller_tag (id/label/color). Bem mais leve que getAll() (que usa o
  // /v2/search/sellers com ~33 campos e 10 leftJoins).
  //
  // Paginado (SUP-425): grupos com milhares de filiais não cabem numa
  // resposta só. NÃO use o `data` de uma página isolada como "todas as
  // filiais que o usuário acessa" — pra isso existe `lookupIds()`, que é
  // sempre completo. Ver `use-sellers-lookup.ts` pro consumo paginado.
  static lookup = async (
    from = 0,
    size = 500,
  ): Promise<PaginatedData<Seller[]>> => {
    return api
      .get('/v2/sellers/lookup', { params: { from, size } })
      .then((res) => res.data);
  };

  // Fonte de verdade de ACESSO do seletor de filiais: id + active de toda
  // filial que o usuário acessa (ativa ou não — diferente de `lookup()`, que
  // é paginado). Payload mínimo (2 campos, sem joins), por isso
  // deliberadamente sem paginação: só é confiável como "sem acesso" vs.
  // "inativa" (ver handleSave em select-sellers-popup.component.tsx) se for
  // sempre completo.
  static lookupIds = async (): Promise<
    Pick<Seller, 'id' | 'active'>[]
  > => {
    return api.get('/v2/sellers/lookup-ids').then((res) => res.data);
  };

  // O grupo ativo tem filial ativa, independente dos vínculos do usuário.
  // Endpoint novo do dashboard-api; o front tolera a ausência dele (ver
  // no-seller-guard.ts).
  static groupStatus = async (): Promise<{ has_active_sellers?: unknown }> => {
    return api.get('/v2/sellers/group-status').then((res) => res.data);
  };

  // Todas as filiais do grupo pra MASTER/INTERNAL (só as próprias pros demais).
  static grantable = async (): Promise<Pick<Seller, 'id' | 'active'>[]> => {
    return api.get('/v2/sellers/grantable').then((res) => res.data);
  };

  static update = async (body: UpdateSellerParams) => {
    const { id: seller_id, ...params } = body;
    return api.patch(`/v1/seller/${seller_id}`, params).then((res) => res.data);
  };

  static searchCarriers = async (
    body: SellerFilter,
  ): Promise<SellerCarrier[]> => {
    return api
      .post('/sellers-page/v1/search/carriers', body)
      .then((res) => res.data);
  };

  static updateAddress = async (body: UpdateAddressParams) => {
    const { id: seller_id, ...params } = body;
    return api
      .patch(`/v1/seller/${seller_id}/address`, {
        ...params,
        latitude: params.latitude?.toString(),
        longitude: params.longitude?.toString(),
      })
      .then((res) => res.data);
  };
}
